import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { PURGE } from 'redux-persist';

import { refresh } from '~modules/auth/api/api-service/refresh';
import { setAccessToken } from '~modules/auth/store/auth-actions';

import { setTenantAccessToken } from '~tenant-modules/auth/store/tenant-auth-actions';

import { resetAuthSensitiveQueries } from '~system/query/auth-query-cache';
import {
  dispatchStoreAction,
  getStoreState,
} from '~system/store/store-accessor';
import { triggerToast } from '~system/store/toast/toast-slice';

import { FRONTEND_CONFIG } from '~app/config';

import { resolveAuthKey } from './resolve-auth-key';

const isDev = FRONTEND_CONFIG.VITE_NODE_ENV === 'development';
const apiUrl = isDev ? '/api' : FRONTEND_CONFIG.VITE_API_URL;

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _silentAuthFailure?: boolean;
  _skipAuthRefresh?: boolean;
};

export class BaseHttpService {
  public static baseUrl = apiUrl;
  public axiosInstance: AxiosInstance;

  private isRefreshing = false;
  private failedQueue: {
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }[] = [];

  private logoutInProgress = false;

  /**
   * API-запросы, для которых 401 НЕ должен запускать refresh
   */
  private refreshIgnoreRequestPatterns = ['auth/quick-signup', 'auth/signout'];

  /**
   * Frontend routes, на которых 401 НЕ должен запускать refresh
   */
  private refreshIgnorePagePatterns = [
    '/sign-up',
    '/login',
    '/forgot-password',
  ];

  get authKey(): string | null {
    const storeState = getStoreState();

    if (!storeState) return null;

    return resolveAuthKey({
      scope: storeState.scope.scope,
      lessorAccessToken: storeState.auth.accessToken,
      tenantAccessToken: storeState.tenantAuth.accessToken,
    });
  }

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: BaseHttpService.baseUrl,
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (this.authKey) {
          config.headers.Authorization = `Bearer ${this.authKey}`;
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => this.handleErrorResponse(error),
    );
  }

  private getCurrentPathname(): string {
    if (typeof window === 'undefined') return '';
    return window.location.pathname;
  }

  private matchesPattern(value: string, patterns: string[]) {
    return patterns.some((pattern) => value.includes(pattern));
  }

  private shouldSkipRefresh(originalRequest?: RetryableRequestConfig): boolean {
    const requestUrl = originalRequest?.url ?? '';
    const pathname = this.getCurrentPathname();

    if (originalRequest?._skipAuthRefresh) return true;

    const isIgnoredRequest = this.matchesPattern(
      requestUrl,
      this.refreshIgnoreRequestPatterns,
    );

    const isIgnoredPage = this.matchesPattern(
      pathname,
      this.refreshIgnorePagePatterns,
    );

    return isIgnoredRequest || isIgnoredPage;
  }

  private async forceLogout({ resetQueries = true, showToast = true } = {}) {
    if (this.logoutInProgress) {
      return Promise.reject(new Error('UNAUTHORIZED'));
    }

    this.logoutInProgress = true;

    try {
      if (resetQueries) {
        resetAuthSensitiveQueries();
      }

      dispatchStoreAction(setAccessToken(null));
      dispatchStoreAction(setTenantAccessToken(null));
      dispatchStoreAction({ type: PURGE, key: 'auth' });
      dispatchStoreAction({ type: PURGE, key: 'tenantAuth' });

      if (showToast) {
        dispatchStoreAction(
          triggerToast({
            title: 'Потрібен повторний вхід',
            message: 'Час дії сесії вичерпано. Будь ласка, увійдіть ще раз.',
            type: 'warning',
          }),
        );
      }
    } finally {
      this.logoutInProgress = false;
    }

    return Promise.reject(new Error('UNAUTHORIZED'));
  }

  private async refreshAccessToken(): Promise<string> {
    const scopeBeforeRefresh = getStoreState()?.scope.scope;
    const { access_token: newToken } = await refresh();
    const scopeAfterRefresh = getStoreState()?.scope.scope;

    if (scopeBeforeRefresh !== scopeAfterRefresh) {
      throw new Error('AUTH_SCOPE_CHANGED_DURING_REFRESH');
    }

    const scopeSetter =
      scopeAfterRefresh === 'lessor' ? setAccessToken : setTenantAccessToken;

    dispatchStoreAction(scopeSetter(newToken));

    return newToken;
  }

  private async handleErrorResponse(error: AxiosError): Promise<unknown> {
    const status = error.response?.status;
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (status === 422) {
      return Promise.reject(error);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    const isRefreshCall =
      originalRequest.url?.includes('auth/refresh') ?? false;

    if (isRefreshCall) {
      if (this.authKey) {
        return this.forceLogout();
      }
      return Promise.reject(error);
    }

    /**
     * Не запускаем refresh для whitelisted route/request
     */
    if (this.shouldSkipRefresh(originalRequest)) {
      return Promise.reject(error);
    }

    /**
     * Защита от бесконечного ретрая
     */
    if (originalRequest._retry) {
      return this.forceLogout();
    }

    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return this.axiosInstance(originalRequest);
      });
    }

    this.isRefreshing = true;
    originalRequest._retry = true;
    try {
      const newToken = await this.refreshAccessToken();

      this.failedQueue.forEach((p) => p.resolve(newToken));
      this.failedQueue = [];

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return this.axiosInstance(originalRequest);
    } catch (err) {
      this.failedQueue.forEach((p) => p.reject(err));
      this.failedQueue = [];

      return this.forceLogout({
        resetQueries: !originalRequest._silentAuthFailure,
        showToast: !originalRequest._silentAuthFailure,
      });
    } finally {
      this.isRefreshing = false;
    }
  }
}
